import { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Clock, CheckCircle2, AlertCircle, Trash2, Gavel } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
interface RFP {
  id: string;
  title: string;
  description: string;
  submission_deadline: string;
  auction_start: string;
  auction_end: string;
  status: string;
  created_at: string;
  _count?: {
    proposals: number;
    bids: number;
  };
}
const LiveAuctions = () => {
  const context = useOutletContext();
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [rfps, setRfps] = useState<RFP[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rfpToDelete, setRfpToDelete] = useState<RFP | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  if (context === "title-only") {
    return "Live Auctions";
  }
  useEffect(() => {
    fetchRFPs();

    // Set up real-time subscription for RFP updates
    const rfpChannel = supabase.channel('rfp-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'rfps'
    }, () => {
      fetchRFPs();
    }).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'proposals'
    }, () => {
      fetchRFPs();
    }).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'bids'
    }, () => {
      fetchRFPs();
    }).subscribe();
    return () => {
      supabase.removeChannel(rfpChannel);
    };
  }, []);
  const fetchRFPs = async () => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/signin");
        return;
      }
      const {
        data: rfpsData,
        error
      } = await supabase.from("rfps").select("*").eq("buyer_id", user.id).eq("deleted_by_buyer", false).neq("status", "COMPLETED").order("created_at", {
        ascending: false
      });
      if (error) throw error;

      // Fetch counts for each RFP
      const rfpsWithCounts = await Promise.all((rfpsData || []).map(async rfp => {
        const {
          count: proposalCount
        } = await supabase.from("proposals").select("*", {
          count: "exact",
          head: true
        }).eq("rfp_id", rfp.id);
        const {
          count: bidCount
        } = await supabase.from("bids").select("*", {
          count: "exact",
          head: true
        }).eq("rfp_id", rfp.id);
        return {
          ...rfp,
          _count: {
            proposals: proposalCount || 0,
            bids: bidCount || 0
          }
        };
      }));
      setRfps(rfpsWithCounts);
    } catch (error: any) {
      console.error("Error fetching RFPs:", error);
      toast({
        title: "Error",
        description: "Failed to load RFPs. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteClick = (e: React.MouseEvent, rfp: RFP) => {
    e.stopPropagation();
    setRfpToDelete(rfp);
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = async () => {
    if (!rfpToDelete) return;
    setIsDeleting(true);
    try {
      // Soft delete - mark as deleted by buyer instead of hard delete
      const {
        error
      } = await supabase.from("rfps").update({ deleted_by_buyer: true }).eq("id", rfpToDelete.id);
      if (error) throw error;
      toast({
        title: "RFP Removed",
        description: `"${rfpToDelete.title}" has been removed from your dashboard.`
      });
      setRfps(rfps.filter(r => r.id !== rfpToDelete.id));
    } catch (error: any) {
      console.error("Error removing RFP:", error);
      toast({
        title: "Error",
        description: "Failed to remove RFP. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setRfpToDelete(null);
    }
  };
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, {
      label: string;
      variant: "default" | "secondary" | "destructive" | "outline";
    }> = {
      DRAFT: {
        label: "Draft",
        variant: "secondary"
      },
      OPEN_FOR_SUBMISSIONS: {
        label: "Open",
        variant: "default"
      },
      AWAITING_AUCTION: {
        label: "Awaiting Auction",
        variant: "outline"
      },
      AUCTION_ACTIVE: {
        label: "Live Auction",
        variant: "destructive"
      },
      AUCTION_ENDED: {
        label: "Ended",
        variant: "secondary"
      },
      COMPLETED: {
        label: "Completed",
        variant: "secondary"
      }
    };
    const config = statusConfig[status] || {
      label: status,
      variant: "outline"
    };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };
  const getStatusIcon = (status: string) => {
    if (status === "AUCTION_ACTIVE") return <AlertCircle className="h-5 w-5 text-red-500 animate-pulse" />;
    if (status === "COMPLETED") return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    return <Clock className="h-5 w-5 text-gray-500" />;
  };
  if (loading) {
    return <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>;
  }
  return <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Reverse Auctions</h2>
        <p className="text-gray-600 mt-1">Manage your live auctions</p>
      </div>

      {rfps.length === 0 ? <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">No Live Auctions</h3>
          </CardContent>
        </Card> : <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rfps.map(rfp => <Card key={rfp.id} className="hover:shadow-lg transition-shadow cursor-pointer relative group flex flex-col min-h-[280px]" onClick={() => navigate(`/auctions/${rfp.id}/bids`)}>
              <CardHeader className="flex-shrink-0">
                <div className="flex items-start justify-between">
                  {/* Title with fixed 2-line height for alignment */}
                  <CardTitle className="text-lg flex-1 pr-8 line-clamp-2 min-h-[3.5rem]">{rfp.title}</CardTitle>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10" onClick={e => handleDeleteClick(e, rfp)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {getStatusIcon(rfp.status)}
                  </div>
                </div>
                <div className="mt-2">{getStatusBadge(rfp.status)}</div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{rfp.description}</p>
                
                <div className="space-y-2 text-sm flex-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Proposals:</span>
                    <span className="font-semibold">{rfp._count?.proposals || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bids:</span>
                    <span className="font-semibold">{rfp._count?.bids || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Deadline:</span>
                    <span className="font-semibold">
                      {new Date(rfp.submission_deadline).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auction:</span>
                    <span className="font-semibold">
                      {new Date(rfp.auction_start).toLocaleDateString()}
                    </span>
                  </div>
                  {/* Active suppliers indicator for live auctions */}
                  {rfp.status === "AUCTION_ACTIVE" && rfp._count?.proposals && rfp._count.proposals > 0 && <div className="flex justify-between">
                      <span className="text-gray-600">Active Suppliers:</span>
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        {rfp._count.proposals} participating
                      </Badge>
                    </div>}
                </div>

                {/* Footer pinned to bottom */}
                <Button variant="outline" size="sm" className="w-full mt-4" onClick={e => {
            e.stopPropagation();
            navigate(`/auctions/${rfp.id}/bids`);
          }}>
                  <Gavel className="h-4 w-4 mr-2" />
                  View Bids
                </Button>
              </CardContent>
            </Card>)}
        </div>}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove RFP</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{rfpToDelete?.title}" from your dashboard? Law firms will still be able to see this matter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isDeleting ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
export default LiveAuctions;